<template>
    <div>
        <div class="flex">
            <div class="bg-gray-100 shadow-md p-4" :class="{ 'w-full': fromReatlas, 'mr-4 lg:w-2/5': showChart && ! fromReatlas, 'lg:w-3/4': ! showChart && ! fromReatlas  }">
                <div class="flex justify-between items-center mb-4">
                    <div class="flex items-center">
                        <label class="mb-0 uppercase text-lg text-gray-900 mr-2" for="scenario">Scenario</label>
                        <select name="scenario" class="mt-0 form-select form-select-blue" v-model="ksds.selectedScenarioId">
                            <option v-for="scenario in ksds.scenarios" :key="scenario.id" :value="scenario.id" :selected="scenario.id == ksds.selectedScenarioId">{{ scenario.name }}</option>
                        </select>
                        <v-popover offset="10">
                            <button class="ml-2">
                                <inline-svg name="settings"></inline-svg>
                            </button>

                            <template slot="popover">
                                <div class="p-3">
                                    <h1>Scoring Options</h1>
                                    <div class="mt-6 space-y-5">
                                        <div v-if="hasPrice" class="flex justify-between space-x-10">
                                            <span class="text-sm">Use Price</span>
                                            <toggle-button @change="calculateCompScore" v-model="usePrice" color="#38C172" sync></toggle-button>
                                        </div>
                                    </div>
                                    <div class="mt-6 flex justify-center">
                                        <button class="btn btn-blue" v-close-popover>Close</button>
                                    </div>
                                </div>
                            </template>
                        </v-popover>
                    </div>

                    <div class="flex items-center justify-center">
                        <div
                            class="flex items-center justify-center w-12 h-12 p-6 rounded-full shadow-lg font-black text-base text-white"
                            :class="scoreColor">
                            {{ compScore }}
                        </div>
                    </div>
                </div>

                <div class="w-full">
                    <div v-if="scenarioKsds.length">
                        <table class="w-full">
                            <thead>
                                <tr>
                                    <th class="pl-4">Driver</th>
                                    <th class="bg-blue-500 px-2">
                                        <div class="flex justify-between">
                                            <span>Poor</span>
                                            <span>Perfect</span>
                                        </div>
                                    </th>
                                    <th class="text-right">Weight (%)</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(ksd, ksdIndex) in scenarioKsds" :key="ksdIndex">
                                    <td class="pl-4 text-base">{{ ksd.driver }}</td>                                        
                                    <td class="px-4 bg-blue-500">
                                        <input v-if="ksd.driver != 'Price'" @input="calculateCompScore()" v-model.number="ksd.score" type="range" min="0" max="5" step="1" class="range-blue">
                                    </td>
                                    <td class="text-right">
                                        <div class="text-base">{{ ksd.weight }}%</div>
                                    </td>
                                </tr>
                            </tbody>
                            <tfoot class="border-t border-gray-light">
                                <tr>
                                    <td class="font-medium pl-6">Unallocated Percentage</td>
                                    <td></td>
                                    <td class="text-right text-base">{{ unallocatedPercentage }}%</td>
                                </tr>
                            </tfoot>
                        </table>
                        <button @click="saveKSDScores" class="btn btn-green float-right mt-4">Save</button>
                    </div>
                    <div v-else class="mt-10">
                        <p class="m-6 text-gray text-2xl text-center">No KSDs Found</p>
                    </div>
                </div>
            </div>

            <div v-if="showChart" class="flex flex-col h-full w-3/5">   
                <pie-chart
                    :data="chartData"
                    height="480"
                    ref="ksdChart"
                    legend-font-color="#4b5563"
                    bg-color="#ffffff"
                    smart-line-color="#4b5563"
                    label-font-color="#4b5563">
                </pie-chart>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapState, mapGetters } from 'vuex';

    export default {
        props: {
            projectId: {
                type: Number,
                required: true
            },

            scorableId: {
                type: Number,
                required: true
            },

            scorableType: {
                type: String,
                required: true
            },

            data: {
                type: [Array, Object],
                required: true
            },

            isModal: {
                type: Boolean,
                default: false
            },

            showChart: {
                type: Boolean,
                default: true
            },

            fromReatlas: {
                type: Boolean,
                default: false
            }
        },

        components: {
        },

        data() {
            return { 
                usePrice: false,
                useFavoritesOnly: false,
                compScore: 0
            }
        },

        created() {
            // get ksd scores for current comp
            this.setKSDScores();

            // calculate comp score
            this.calculateCompScore();
        },

        watch: {
            scorableId() {
                this.setKSDScores();
                this.calculateCompScore();
            },
            
            'ksds.selectedScenarioId'() {
                this.setKSDScores();
                this.calculateCompScore();
            }
        },

        computed: {
            ...mapState(['projects', 'ksds', 'buildings']),
            ...mapGetters(['scenario', 'scenarioKsds']),

            project() {
                return this.projects.selectedProject;
            },

            projectUnits() {
                return _.flatMap(this.project.comps, (comp) => comp.units);
            },

            comp() {
                return this.buildings.selectedBuilding;
            },

            minProjectUnitRent() {
                return _.minBy(this.projectUnits, function (unit) { return unit.rent_cost; })['rent_cost'] || 0;
            },

            maxProjectUnitRent() {
                return _.maxBy(this.projectUnits, function (unit) { return unit.rent_cost; })['rent_cost'] || 0;
            },

            chartData() {
                return _.map(this.scenarioKsds, ksd => {
                    return { label: ksd.driver, value: ksd.weight };
                });
            },

            unallocatedPercentage() {
                return 100 - parseInt(_.sumBy(this.scenarioKsds, (ksd) => { return parseInt(ksd.weight); }));
            },

            scoreColor() {
                return (this.compScore < 70 ? 'bg-red-500' : (this.compScore >= 90 ? 'bg-green-500' : 'bg-yellow-200 text-yellow-700'));
            },

            isDefaultScenario() {
                return this.scenario.is_default === 1;
            },

            hasPrice() {
                return _.find(this.scenarioKsds, ['driver', 'Price']) != undefined;
            }
        },

        methods: {
            setKSDScores() {
                // get ksd scores for current comp
                _.map(this.scenarioKsds, (ksd) => {
                    const comp = _.find(ksd.scores, { 'scorable_id': this.scorableId, 'scorable_type': this.scorableType });
                    ksd.score = comp ? comp.score : 0;
                });
            },

            calculateCompScore() {
                var compScore = 0;

                const priceKSD = _.find(this.scenarioKsds, ['driver', 'Price']);
                const priceWeight = priceKSD ? parseInt(priceKSD.weight) : 0;

                _.map(this.scenarioKsds, (ksd) => {
                    const score = ksd.score ? parseInt(ksd.score) : 0;
                    const weight = parseInt(ksd.weight);
                    const divisble = this.usePrice ? 100 : 100 - priceWeight;

                    const scorePercentage = score * 20;
                    const weightedScore = (scorePercentage * weight) / divisble;

                    compScore += weightedScore;
                });

                if (this.usePrice) {                
                    let buildingAvgUnitPrice = this.comp.avg_rent;

                    let priceDelta  = (this.maxProjectUnitRent - this.minProjectUnitRent) / 100;
                    let priceScore  = (priceDelta == 0 ? 0 : (((this.maxProjectUnitRent - buildingAvgUnitPrice)/priceDelta)*priceWeight)/100);
                    
                    priceScore  = (buildingAvgUnitPrice == 0 ? 0 : priceScore);

                    compScore += parseFloat(priceScore);
                }

                compScore = Math.round(compScore);

                this.compScore = compScore > 100 ? 100 : compScore;

                this.$emit('comp-score-updated', this.compScore);
            },

            saveKSDScores() {
                // save scores
                var scores = _.map(this.scenarioKsds, (ksd) => {
                    return {
                        ksd_id: ksd.id,
                        scorable_id: this.scorableId,
                        scorable_type: this.scorableType,
                        score: ksd.score
                    }
                });

                axios.patch('/ksds-scores', scores)
                    .then((response) => {
                        this.$notify({
                            type: 'success',
                            title: 'Success',
                            text: response.data.message
                        });
                    })
                    .catch((response) => {
                        console.error(response);
                    })

                // update comp score if default scenario
                if (this.isDefaultScenario || this.ksds.scenarios.length == 1) {
                    axios.patch('/projects/' + this.projectId + '/comps/' + this.scorableId, {
                        score: this.compScore
                    }).then((response) => {
                        this.$emit('comp-score-updated', this.compScore);

                        this.$notify({
                            type: 'success',
                            title: 'Success',
                            text: 'Comp Score Updated'
                        });
                    }).catch((response) => {
                        console.error(response);
                    })
                }

            }
        }
    }
</script>